/**
 * Shared booking pricing constants and pure calculation helpers.
 *
 * This file is intentionally CommonJS so the Node backend can require it
 * directly while the React frontend can import named exports through Babel.
 */

/**
 * @typedef {'truck' | 'trailer' | 'truck_and_trailer'} MonthlyPricingVehicleType
 * @typedef {MonthlyPricingVehicleType | string | null | undefined} MonthlyPricingVehicleTypeInput
 */

const PROMO_DISCOUNT = 0.15;
const DEFAULT_DAILY_RATE = 27;
const DEFAULT_CONTRACT_MONTHLY_RATE = 252;
const DEFAULT_MONTHLY_DISPLAY_RATE = 140;
const DEFAULT_NO_CONTRACT_MONTHLY_RATE = 360;
const MONTHLY_NO_CONTRACT_UPCHARGE = 100;
const MONTHLY_SERVICE_FEE_PERCENTAGE = 0.04;
const SERVICE_FEE_PERCENTAGE = MONTHLY_SERVICE_FEE_PERCENTAGE;
const MARKETPLACE_DRIVER_BOOKING_FEE_PERCENT = 0.3;
const WEEKLY_SAVINGS_PERCENTAGE = 0.048;

const MONTHLY_DISPLAY_MODES = Object.freeze({
  EXACT: 'exact',
  AS_LOW_AS: 'as_low_as',
});

const MONTHLY_PRICING_VEHICLE_TYPES = ['truck', 'trailer', 'truck_and_trailer'];
const MONTHLY_PRICING_VEHICLE_TYPE_ALIASES = Object.freeze({
  truck: 'truck',
  trailer: 'trailer',
  truck_and_trailer: 'truck_and_trailer',
  'truck only': 'truck',
  'trailer only': 'trailer',
  'truck and trailer': 'truck_and_trailer',
});

const getConfiguredMonthlyDisplayMode = (location = null) => (
  location?.monthlyDisplayFixedPrice === true
    ? MONTHLY_DISPLAY_MODES.EXACT
    : MONTHLY_DISPLAY_MODES.AS_LOW_AS
);

const calculateWeeklyFromDaily = (dailyRate) => (
  Math.round(dailyRate * 7 * (1 - WEEKLY_SAVINGS_PERCENTAGE))
);

const getLocationRates = (location) => {
  const dailyRate = location?.dailyRate ?? DEFAULT_DAILY_RATE;
  return {
    daily: dailyRate,
    weekly: calculateWeeklyFromDaily(dailyRate),
  };
};

const getLocationMonthlyDisplayMeta = (location) => {
  const monthlySubscriptionRateCents = Number(location?.monthlySubscriptionRateCents);
  if (Number.isFinite(monthlySubscriptionRateCents) && monthlySubscriptionRateCents > 0) {
    return {
      amount: Number((monthlySubscriptionRateCents / 100).toFixed(2)),
      mode: getConfiguredMonthlyDisplayMode(location),
      isFallbackFloor: false,
    };
  }

  const monthlyRateOH = Number(location?.monthlyRateOH);
  if (Number.isFinite(monthlyRateOH) && monthlyRateOH > 0) {
    return {
      amount: monthlyRateOH,
      mode: getConfiguredMonthlyDisplayMode(location),
      isFallbackFloor: false,
    };
  }

  const monthlyRate = Number(location?.monthlyRate);
  if (Number.isFinite(monthlyRate) && monthlyRate > 0) {
    return {
      amount: monthlyRate,
      mode: getConfiguredMonthlyDisplayMode(location),
      isFallbackFloor: false,
    };
  }

  return {
    amount: DEFAULT_MONTHLY_DISPLAY_RATE,
    mode: MONTHLY_DISPLAY_MODES.AS_LOW_AS,
    isFallbackFloor: true,
  };
};

const getLocationMonthlyDisplayRate = (location) => (
  getLocationMonthlyDisplayMeta(location).amount
);

/**
 * @param {MonthlyPricingVehicleTypeInput} value
 * @returns {MonthlyPricingVehicleType | null}
 */
const normalizeMonthlyPricingVehicleType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return MONTHLY_PRICING_VEHICLE_TYPE_ALIASES[normalized] || null;
};

/**
 * @param {Object | null | undefined} location
 * @param {MonthlyPricingVehicleTypeInput} [vehicleType=null]
 * @param {number} [fallbackRate=DEFAULT_CONTRACT_MONTHLY_RATE]
 * @returns {number}
 */
const getLocationContractMonthlyRate = (
  location,
  vehicleType = null,
  fallbackRate = DEFAULT_CONTRACT_MONTHLY_RATE
) => {
  const normalizedVehicleType = normalizeMonthlyPricingVehicleType(vehicleType);
  if (normalizedVehicleType) {
    const tierRateCents = Number(location?.monthlyVehicleTierRates?.[normalizedVehicleType]);
    if (Number.isFinite(tierRateCents) && tierRateCents > 0) {
      return Number((tierRateCents / 100).toFixed(2));
    }
  }

  const monthlySubscriptionRateCents = Number(location?.monthlySubscriptionRateCents);
  if (Number.isFinite(monthlySubscriptionRateCents) && monthlySubscriptionRateCents > 0) {
    return Number((monthlySubscriptionRateCents / 100).toFixed(2));
  }

  const monthlyRateOH = Number(location?.monthlyRateOH);
  if (Number.isFinite(monthlyRateOH) && monthlyRateOH > 0) {
    return monthlyRateOH;
  }

  const monthlyRate = Number(location?.monthlyRate);
  if (Number.isFinite(monthlyRate) && monthlyRate > 0) {
    return monthlyRate;
  }

  const fallback = Number(fallbackRate);
  if (Number.isFinite(fallback) && fallback > 0) {
    return Number(fallback.toFixed(2));
  }

  return DEFAULT_CONTRACT_MONTHLY_RATE;
};

const getLocationLowestMonthlyContractMeta = (location) => {
  const candidates = [];

  const truckTierCents = Number(location?.monthlyVehicleTierRates?.truck);
  if (Number.isFinite(truckTierCents) && truckTierCents > 0) {
    candidates.push({
      amount: Number((truckTierCents / 100).toFixed(2)),
      source: 'truck_tier',
    });
  }

  const monthlySubscriptionRateCents = Number(location?.monthlySubscriptionRateCents);
  if (Number.isFinite(monthlySubscriptionRateCents) && monthlySubscriptionRateCents > 0) {
    candidates.push({
      amount: Number((monthlySubscriptionRateCents / 100).toFixed(2)),
      source: 'base_rate',
    });
  }

  const monthlyRateOH = Number(location?.monthlyRateOH);
  if (Number.isFinite(monthlyRateOH) && monthlyRateOH > 0) {
    candidates.push({
      amount: monthlyRateOH,
      source: 'legacy_monthly_rate_oh',
    });
  }

  const monthlyRate = Number(location?.monthlyRate);
  if (Number.isFinite(monthlyRate) && monthlyRate > 0) {
    candidates.push({
      amount: monthlyRate,
      source: 'legacy_monthly_rate',
    });
  }

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((lowest, candidate) => (
    !lowest || candidate.amount < lowest.amount ? candidate : lowest
  ), null);
};

/**
 * @param {Object | null | undefined} location
 * @param {number} [fallbackRate=DEFAULT_NO_CONTRACT_MONTHLY_RATE]
 * @param {MonthlyPricingVehicleTypeInput} [vehicleType=null]
 * @returns {number}
 */
const getLocationNoContractRate = (
  location,
  fallbackRate = DEFAULT_NO_CONTRACT_MONTHLY_RATE,
  vehicleType = null
) => {
  const normalizedVehicleType = normalizeMonthlyPricingVehicleType(vehicleType);
  if (normalizedVehicleType) {
    const contractMonthlyRate = getLocationContractMonthlyRate(location, normalizedVehicleType, 0);
    if (contractMonthlyRate > 0) {
      return Number((contractMonthlyRate + MONTHLY_NO_CONTRACT_UPCHARGE).toFixed(2));
    }
  }

  const lowestMonthlyContract = getLocationLowestMonthlyContractMeta(location);
  if (lowestMonthlyContract) {
    return Number((lowestMonthlyContract.amount + MONTHLY_NO_CONTRACT_UPCHARGE).toFixed(2));
  }

  const fallback = Number(fallbackRate);
  if (Number.isFinite(fallback) && fallback > 0) {
    return Number(fallback.toFixed(2));
  }

  return DEFAULT_NO_CONTRACT_MONTHLY_RATE;
};

/**
 * @param {Object | null | undefined} location
 * @param {MonthlyPricingVehicleTypeInput} [vehicleType=null]
 * @param {number | null | undefined} [fallbackRateCents=0]
 * @returns {number}
 */
const getEffectiveLocationMonthlyRateCents = (
  location,
  vehicleType = null,
  fallbackRateCents = 0
) => {
  const normalizedVehicleType = normalizeMonthlyPricingVehicleType(vehicleType);
  if (normalizedVehicleType) {
    const tierRate = Number(location?.monthlyVehicleTierRates?.[normalizedVehicleType]);
    if (Number.isFinite(tierRate) && tierRate > 0) {
      return Math.round(tierRate);
    }
  }

  const baseRate = Number(location?.monthlySubscriptionRateCents ?? location?.monthlyRateCents ?? 0);
  if (Number.isFinite(baseRate) && baseRate > 0) {
    return Math.round(baseRate);
  }

  const fallback = Number(fallbackRateCents || 0);
  if (Number.isFinite(fallback) && fallback > 0) {
    return Math.round(fallback);
  }

  return 0;
};

const applyMonthlyServiceFeeToCents = (baseAmountCents, waiveServiceFee = false) => {
  const baseAmount = Math.max(0, Math.round(Number(baseAmountCents || 0)));
  const standardServiceFeeCents = Math.round(baseAmount * MONTHLY_SERVICE_FEE_PERCENTAGE);
  if (waiveServiceFee) {
    return {
      baseAmountCents: baseAmount,
      serviceFeeCents: 0,
      standardServiceFeeCents,
      waivedSavingsCents: standardServiceFeeCents,
      serviceFeeWaived: true,
      totalAmountCents: baseAmount,
    };
  }

  return {
    baseAmountCents: baseAmount,
    serviceFeeCents: standardServiceFeeCents,
    standardServiceFeeCents,
    waivedSavingsCents: 0,
    serviceFeeWaived: false,
    totalAmountCents: baseAmount + standardServiceFeeCents,
  };
};

const getLocationPromoDiscount = (location) => {
  if (location?.promoDiscountPercentage !== null && location?.promoDiscountPercentage !== undefined) {
    return location.promoDiscountPercentage / 100;
  }
  return PROMO_DISCOUNT;
};

const calculatePromoPrice = (rate, discountPercentage = PROMO_DISCOUNT) => (
  Math.round(rate * (1 - discountPercentage))
);

const getPromoDiscountLabel = (location = null) => {
  const discount = location ? getLocationPromoDiscount(location) : PROMO_DISCOUNT;
  return `${Math.round(discount * 100)}%`;
};

const resolveMonthlyPlanType = (formData = {}, totalDays = 0) => {
  const selectedPlan = String(formData.contractType ?? formData.parkingDuration ?? '').trim().toLowerCase();
  if (selectedPlan === 'no contract') {
    return 'no contract';
  }
  if (selectedPlan === 'per month' || selectedPlan === 'contract') {
    return 'contract';
  }
  return Number(totalDays) >= 30 ? 'no contract' : 'contract';
};

const getBookingVehicles = (formData = {}) => {
  if (formData.bookingType === 'fleet' && Array.isArray(formData.fleetVehicles) && formData.fleetVehicles.length > 0) {
    return formData.fleetVehicles;
  }

  return [{ vehicleType: formData.vehicleType || null }];
};

const calculateVehicleBaseAmount = ({
  totalDays,
  contractType,
  dailyRate,
  weeklyRate,
  contractMonthlyRate,
  noContractRate,
}) => {
  if (totalDays <= 6) {
    return totalDays * dailyRate;
  }

  if (totalDays === 7) {
    return weeklyRate;
  }

  if (totalDays > 7 && totalDays < 30) {
    const weeks = Math.floor(totalDays / 7);
    const remainingDays = totalDays % 7;
    return weeks * weeklyRate + remainingDays * dailyRate;
  }

  const months = Math.floor(totalDays / 30);
  const remainingDays = totalDays % 30;
  let baseAmount = months * (contractType === 'no contract' ? noContractRate : contractMonthlyRate);

  if (remainingDays >= 7) {
    const weeks = Math.floor(remainingDays / 7);
    const extraDays = remainingDays % 7;
    baseAmount += weeks * weeklyRate + extraDays * dailyRate;
  } else if (remainingDays > 0) {
    baseAmount += remainingDays * dailyRate;
  }

  return baseAmount;
};

const calculateCheckoutServiceFee = (totalBaseAmount, location = null) => {
  if (location?.paymentProvider !== 'stripe') {
    return totalBaseAmount * SERVICE_FEE_PERCENTAGE;
  }
  const preview = location?.marketplaceFeePreview || {};
  const percent = Number(preview.customerFeePercent);
  const fixedCents = Number(preview.customerFeeFixedCents);
  const minimumCents = Number(preview.customerFeeMinimumCents);
  if (![percent, fixedCents, minimumCents].every(Number.isFinite)) {
    // Never show the legacy Square fee on a Stripe location. The authoritative
    // Stripe quote replaces this preview before the customer can submit payment.
    return 0;
  }
  const calculatedCents = Math.round(totalBaseAmount * 100 * (percent / 100)) + fixedCents;
  return Math.max(calculatedCents, minimumCents) / 100;
};

const calculateServiceFeeSavings = ({
  parkingSubtotalCents,
  customerFeeCents,
  customerFeePercent,
  benchmarkPercent,
  benchmarkLabel = '',
} = {}) => {
  const subtotal = Number(parkingSubtotalCents);
  const chargedFee = Number(customerFeeCents);
  const chargedPercent = Number(customerFeePercent);
  const comparisonPercent = Number(benchmarkPercent);
  if (![subtotal, chargedFee, chargedPercent, comparisonPercent].every(Number.isFinite)
    || subtotal <= 0
    || chargedFee < 0
    || comparisonPercent <= chargedPercent) {
    return null;
  }

  const benchmarkFeeCents = Math.round(subtotal * (comparisonPercent / 100));
  const savingsCents = Math.max(0, benchmarkFeeCents - chargedFee);
  if (savingsCents <= 0) return null;

  return {
    benchmarkFeeCents,
    savingsCents,
    percentagePoints: comparisonPercent - chargedPercent,
    benchmarkPercent: comparisonPercent,
    benchmarkLabel: String(benchmarkLabel || '').trim(),
  };
};

const isValidDate = (dateString) => {
  if (!dateString) return false;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  const [year, month, day] = dateString.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  return dateObj.getFullYear() === year
    && dateObj.getMonth() === month - 1
    && dateObj.getDate() === day;
};

const getTotalDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  if (!isValidDate(startDate) || !isValidDate(endDate)) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (end < start) return 0;

  const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
  if (start.toDateString() === end.toDateString()) {
    return 1;
  }

  return totalDays;
};

const calculateSavings = (duration, dailyRate, weeklyRate, monthlyRate, noContractRate = monthlyRate) => {
  if (
    dailyRate === undefined
    || weeklyRate === undefined
    || monthlyRate === undefined
    || noContractRate === undefined
  ) {
    return 0;
  }

  if (dailyRate < 0 || weeklyRate < 0 || monthlyRate < 0 || noContractRate < 0) {
    return 0;
  }

  const daysInWeek = 7;
  const daysInMonth = 30;

  if (duration === 'per week') {
    return dailyRate * daysInWeek - weeklyRate;
  }
  if (duration === 'per month') {
    return dailyRate * daysInMonth - monthlyRate;
  }
  if (duration === 'no contract') {
    return dailyRate * daysInMonth - noContractRate;
  }
  return 0;
};

const calculateBookingAmount = (formData = {}, location = null) => {
  const { startDate, endDate } = formData;
  const totalDays = getTotalDays(startDate, endDate);

  if (totalDays === 0) return 0;

  const locationRates = location ? getLocationRates(location) : {
    daily: DEFAULT_DAILY_RATE,
    weekly: calculateWeeklyFromDaily(DEFAULT_DAILY_RATE),
  };
  const baseDailyRate = locationRates.daily;
  const baseWeeklyRate = locationRates.weekly;
  const monthlyPlanType = resolveMonthlyPlanType(formData, totalDays);
  const bookingVehicles = getBookingVehicles(formData);
  const isPromoActive = location?.isPromoActive ?? formData.isPromoActive ?? false;

  let dailyRate = baseDailyRate;
  let weeklyRate = baseWeeklyRate;

  if (isPromoActive) {
    const discountPercentage = location ? getLocationPromoDiscount(location) : PROMO_DISCOUNT;
    dailyRate = baseDailyRate * (1 - discountPercentage);
    weeklyRate = baseWeeklyRate * (1 - discountPercentage);
  }

  const totalBaseAmount = bookingVehicles.reduce((sum, vehicle) => {
    const contractMonthlyRate = getLocationContractMonthlyRate(
      location,
      vehicle?.vehicleType,
      DEFAULT_CONTRACT_MONTHLY_RATE
    );
    const noContractRate = getLocationNoContractRate(
      location,
      DEFAULT_NO_CONTRACT_MONTHLY_RATE,
      vehicle?.vehicleType
    );
    return sum + calculateVehicleBaseAmount({
      totalDays,
      contractType: monthlyPlanType,
      dailyRate,
      weeklyRate,
      contractMonthlyRate,
      noContractRate,
    });
  }, 0);

  return Math.round((totalBaseAmount + calculateCheckoutServiceFee(totalBaseAmount, location)) * 100);
};

const calculatePriceBreakdown = (formData = {}, location = null) => {
  const { startDate, endDate } = formData;
  const totalDays = getTotalDays(startDate, endDate);

  if (totalDays === 0) {
    return { basePrice: 0, serviceFee: 0, totalPrice: 0, vehicleCount: 0 };
  }

  const locationRates = location ? getLocationRates(location) : {
    daily: DEFAULT_DAILY_RATE,
    weekly: calculateWeeklyFromDaily(DEFAULT_DAILY_RATE),
  };
  const baseDailyRate = locationRates.daily;
  const baseWeeklyRate = locationRates.weekly;
  const monthlyPlanType = resolveMonthlyPlanType(formData, totalDays);
  const bookingVehicles = getBookingVehicles(formData);
  const isPromoActive = location?.isPromoActive ?? formData.isPromoActive ?? false;

  let dailyRate = baseDailyRate;
  let weeklyRate = baseWeeklyRate;

  if (isPromoActive) {
    const discountPercentage = location ? getLocationPromoDiscount(location) : PROMO_DISCOUNT;
    dailyRate = baseDailyRate * (1 - discountPercentage);
    weeklyRate = baseWeeklyRate * (1 - discountPercentage);
  }

  const perVehicleBasePrices = bookingVehicles.map((vehicle) => {
    const contractMonthlyRate = getLocationContractMonthlyRate(
      location,
      vehicle?.vehicleType,
      DEFAULT_CONTRACT_MONTHLY_RATE
    );
    const noContractRate = getLocationNoContractRate(
      location,
      DEFAULT_NO_CONTRACT_MONTHLY_RATE,
      vehicle?.vehicleType
    );
    return calculateVehicleBaseAmount({
      totalDays,
      contractType: monthlyPlanType,
      dailyRate,
      weeklyRate,
      contractMonthlyRate,
      noContractRate,
    });
  });

  const vehicleCount = perVehicleBasePrices.length;
  const totalBaseAmount = perVehicleBasePrices.reduce((sum, amount) => sum + amount, 0);
  const serviceFee = calculateCheckoutServiceFee(totalBaseAmount, location);
  const totalAmount = totalBaseAmount + serviceFee;

  return {
    basePrice: totalBaseAmount,
    serviceFee,
    totalPrice: totalAmount,
    vehicleCount,
    pricePerVehicle: vehicleCount > 0 ? totalBaseAmount / vehicleCount : 0,
  };
};

const formatMonthlyAmount = (amount) => Number(amount).toFixed(Number.isInteger(amount) ? 0 : 2);

/**
 * @param {{
 *   location?: any,
 *   duration?: string | null | undefined,
 *   monthlyDisplayMode?: 'exact' | 'as_low_as' | null | undefined,
 *   displayedPrice?: number | string | null | undefined,
 *   vehicleType?: MonthlyPricingVehicleTypeInput,
 *   fleetVehicles?: Array<{ vehicleType?: MonthlyPricingVehicleTypeInput }> | null | undefined
 * }} [options]
 * @returns {{
 *   applies: boolean,
 *   message: string,
 *   helperText: string,
 *   currentRate: number | null,
 *   displayedRate: number | null
 * } | null}
 */
const getCheckoutMonthlyPricingContext = ({
  location = null,
  duration = null,
  monthlyDisplayMode = null,
  displayedPrice = null,
  vehicleType = null,
  fleetVehicles = null,
} = {}) => {
  const normalizedDuration = String(duration || '').trim().toLowerCase();
  const isMonthlyPlan = normalizedDuration === 'per month' || normalizedDuration === 'no contract';
  if (!isMonthlyPlan || monthlyDisplayMode !== MONTHLY_DISPLAY_MODES.AS_LOW_AS) {
    return null;
  }

  const displayedRate = Number(displayedPrice);
  if (!Number.isFinite(displayedRate) || displayedRate <= 0) {
    return null;
  }

  const getCurrentVehicleRate = (selectedVehicleType) => (
    normalizedDuration === 'no contract'
      ? getLocationNoContractRate(location, DEFAULT_NO_CONTRACT_MONTHLY_RATE, selectedVehicleType)
      : getLocationContractMonthlyRate(location, selectedVehicleType, DEFAULT_CONTRACT_MONTHLY_RATE)
  );

  const vehicleEntries = Array.isArray(fleetVehicles) && fleetVehicles.length > 0
    ? fleetVehicles
    : [{ vehicleType }];
  const rateEntries = vehicleEntries
    .map((entry) => {
      const selectedVehicleType = entry?.vehicleType || null;
      return {
        vehicleType: selectedVehicleType,
        rate: getCurrentVehicleRate(selectedVehicleType),
      };
    })
    .filter((entry) => Number.isFinite(entry.rate) && entry.rate > 0);

  if (rateEntries.length === 0) {
    return null;
  }

  const highestRateEntry = rateEntries.reduce((highest, entry) => (
    !highest || entry.rate > highest.rate ? entry : highest
  ), null);

  if (!highestRateEntry || highestRateEntry.rate <= displayedRate) {
    return null;
  }

  const planLabel = normalizedDuration === 'no contract' ? 'no-contract monthly' : 'monthly';
  const normalizedVehicleType = normalizeMonthlyPricingVehicleType(highestRateEntry.vehicleType);
  const isFleet = Array.isArray(fleetVehicles) && fleetVehicles.length > 0;

  if (isFleet) {
    return {
      applies: true,
      displayedRate,
      currentRate: highestRateEntry.rate,
      helperText: `Displayed pricing reflects the lowest available ${planLabel} rate per vehicle.`,
      message: `Rates page pricing showed the lowest available ${planLabel} rate per vehicle. Checkout updates by each vehicle type, so truck-and-trailer units price above the entry-tier amount.`,
    };
  }

  const vehicleTypeLabel = normalizedVehicleType === 'truck'
    ? 'Truck Only'
    : normalizedVehicleType === 'trailer'
      ? 'Trailer Only'
      : 'Truck and Trailer';

  return {
    applies: true,
    displayedRate,
    currentRate: highestRateEntry.rate,
    helperText: `Displayed pricing reflects the lowest available ${planLabel} rate.`,
    message: `Rates page pricing showed the lowest available ${planLabel} rate. Your current ${vehicleTypeLabel} selection is $${formatMonthlyAmount(highestRateEntry.rate)}/month.`,
  };
};

module.exports = {
  PROMO_DISCOUNT,
  DEFAULT_DAILY_RATE,
  DEFAULT_CONTRACT_MONTHLY_RATE,
  DEFAULT_MONTHLY_DISPLAY_RATE,
  DEFAULT_NO_CONTRACT_MONTHLY_RATE,
  MONTHLY_NO_CONTRACT_UPCHARGE,
  MONTHLY_SERVICE_FEE_PERCENTAGE,
  SERVICE_FEE_PERCENTAGE,
  MARKETPLACE_DRIVER_BOOKING_FEE_PERCENT,
  MONTHLY_DISPLAY_MODES,
  WEEKLY_SAVINGS_PERCENTAGE,
  MONTHLY_PRICING_VEHICLE_TYPES,
  calculateWeeklyFromDaily,
  getLocationRates,
  getLocationMonthlyDisplayMeta,
  getLocationMonthlyDisplayRate,
  getLocationContractMonthlyRate,
  getLocationLowestMonthlyContractMeta,
  getLocationNoContractRate,
  normalizeMonthlyPricingVehicleType,
  getEffectiveLocationMonthlyRateCents,
  applyMonthlyServiceFeeToCents,
  getLocationPromoDiscount,
  calculatePromoPrice,
  getPromoDiscountLabel,
  resolveMonthlyPlanType,
  getTotalDays,
  calculateSavings,
  calculateBookingAmount,
  calculatePriceBreakdown,
  calculateCheckoutServiceFee,
  calculateServiceFeeSavings,
  getCheckoutMonthlyPricingContext,
};
